/**
 * Helpers for reading DRF error responses.
 *
 * `error.response.data` is normally an **object** (`{"detail": "..."}` or
 * `{"error_code": "...", "message": "..."}`), but depending on the response type
 * it can also arrive as a raw string. Never assume one of the two — these helpers
 * must not throw, otherwise the real error message is lost for the user.
 */

/** HTTP status of an axios-style error, or `undefined` if the request never got a response. */
export function getApiErrorStatus(error: any): number | undefined {
  return error?.response?.status
}

/** The error body as an object, or `undefined` when it is neither an object nor parseable JSON. */
function getApiErrorData(error: any): Record<string, any> | undefined {
  const data = error?.response?.data

  if (typeof data === 'object' && data !== null) {
    return data as Record<string, any>
  }

  // Fallback: some responses arrive as an unparsed JSON string.
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data)
      return typeof parsed === 'object' && parsed !== null ? parsed : undefined
    } catch {
      return undefined
    }
  }

  return undefined
}

/** The backend's `error_code`, or `undefined` when the response carries none. */
export function getApiErrorCode(error: any): string | undefined {
  const code = getApiErrorData(error)?.error_code
  return typeof code === 'string' ? code : undefined
}

/**
 * The backend's validation messages for one field of `form_error`, e.g.
 * `getApiFormErrors(error, 'new_password')`. They are written for the user and
 * are meant to be shown as they are. Empty array when there are none.
 */
export function getApiFormErrors(error: any, field: string): string[] {
  const messages = getApiErrorData(error)?.form_error?.[field]
  if (Array.isArray(messages)) {
    return messages.filter((message): message is string => typeof message === 'string')
  }
  return typeof messages === 'string' ? [messages] : []
}
